// index.tsx
// Route configuration for the Athena EHR experience.
// Sets up navigation, context providers, and route logic for Athena-specific flows.

import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from "react-router-dom";
import { NavItem } from "@/types/navigation";
import MainNav from "@components/MainNav";
import { styled } from "@stitches/react";
import { OrdersContextProvider } from "@contexts/orders-context";
import MyTasks from "./MyTasks";
import MySurgeons from "./MySurgeons";
import { useGetSubscribedSurgeons } from "@/hooks/use-surgeons";
import Loader from "@/components/Loader";
import { SurgeonSubscribed } from "@/types/surgeons";

const RoutesWrapper = styled("div", {});

/**
 * Route definitions for Athena navigation.
 */
const routes: NavItem[] = [
  {
    id: "my-tasks",
    text: "My Tasks",
    href: "/my-tasks",
    element: (
      <OrdersContextProvider>
        <MyTasks />
      </OrdersContextProvider>
    ),
  },
  {
    id: "my-surgeons",
    text: "My Surgeons",
    href: "/surgeons",
    element: <MySurgeons />,
  },
];

/**
 * Determines the default route based on the user's subscribed surgeons.
 * - If there are no surgeons or any are disabled, defaults to /surgeons.
 * - Otherwise, defaults to /my-tasks.
 * @param surgeons - List of subscribed surgeons
 * @returns The default route path
 */
const getDefaultRoute = (surgeons: SurgeonSubscribed[] | undefined) => {
  const hasDisabledSurgeons = surgeons?.some(
    (surgeon) => surgeon.providerActive === false,
  );

  if (surgeons) {
    if (hasDisabledSurgeons || surgeons.length === 0) {
      return "/surgeons";
    }
    return "/my-tasks";
  }
  return "/";
};

/**
 * Wrapper for Athena app layout, including navigation and outlet.
 */
const AthenaAppWrapper = () => {
  return (
    <div data-testid="athena-app-wrapper">
      <MainNav navItems={routes} />
      <Outlet />
    </div>
  );
};

/**
 * AthenaRoutes Component
 * Sets up Athena-specific routes, navigation, and context providers.
 * Handles loading and default route logic based on subscribed surgeons.
 */
const AthenaRoutes = () => {
  const {
    data: surgeons,
    isLoading,
    error,
    isError,
  } = useGetSubscribedSurgeons();

  /**
   * Creates the Athena route structure, including default route logic.
   */
  const createRoutes = () => {
    const mappedRoutes = routes.map((route) => {
      return {
        path: route.href,
        element: route.element,
      };
    });

    const defaultRoute = {
      element: <Navigate to={getDefaultRoute(surgeons)} />, // dynamic default
      index: true,
    };

    // Derive basename from the current URL path
    const getBasename = () => {
      return "/";
    };

    const basename = getBasename();

    return createBrowserRouter(
      [
        {
          path: "/",
          id: "root",
          element: <AthenaAppWrapper />,
          children: [defaultRoute, ...mappedRoutes],
        },
      ],
      { basename },
    );
  };

  return (
    <RoutesWrapper>
      <Loader
        displayText="Validating surgeons..."
        isLoading={isLoading}
        errorReason={error?.message}
        isError={isError}
      >
        <RouterProvider router={createRoutes()} />
      </Loader>
    </RoutesWrapper>
  );
};

export default AthenaRoutes;
